import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { IconAlertTriangle, IconCircleCheck, IconCircleX, IconInfoCircle, IconX } from '@tabler/icons-react';
import { useUiStore, type ToastMessage, type ToastType } from '@/stores/uiStore';
import { cn } from '@/utils/cn';

const ICONS: Record<ToastType, typeof IconCircleCheck> = {
  success: IconCircleCheck,
  error: IconCircleX,
  info: IconInfoCircle,
  warning: IconAlertTriangle,
};

const ICON_COLOR: Record<ToastType, string> = {
  success: 'text-state-success',
  error: 'text-state-error',
  info: 'text-state-info',
  warning: 'text-state-warning',
};

function ToastItem({ toast }: { toast: ToastMessage }) {
  const dismissToast = useUiStore((s) => s.dismissToast);
  const Icon = ICONS[toast.type];

  useEffect(() => {
    const timer = setTimeout(() => dismissToast(toast.id), 4000);
    return () => clearTimeout(timer);
  }, [toast.id, dismissToast]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 24 }}
      className="flex w-80 items-start gap-3 rounded-lg border border-border-subtle bg-bg-elevated p-4 shadow-lg"
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', ICON_COLOR[toast.type])} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text-primary">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-xs text-text-secondary">{toast.description}</p>}
      </div>
      <button
        onClick={() => dismissToast(toast.id)}
        className="text-text-tertiary hover:text-text-primary"
        aria-label="Dismiss notification"
      >
        <IconX className="h-4 w-4" />
      </button>
    </motion.div>
  );
}

export function ToastProvider() {
  const toasts = useUiStore((s) => s.toasts);

  return createPortal(
    <div className="fixed right-4 top-4 z-toast flex flex-col gap-2">
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
